import type { SV_COPY } from './en';

/**
 * العربية — the svapp deck (VC-12). Modern Standard Arabic, plain and polite, second person, the register a Gulf
 * representative meets on a government or bank app. Brand and product names (KYC Hub, Chrome, Safari) stay in Latin
 * script; `**bold**` markers and `{slots}` are preserved exactly. Numerals in codes, references and coordinates stay as
 * the server sends them (Latin digits) so a visit code reads the same on screen, aloud and on the stamp.
 *
 * Arabic is a PACK-GATED language: the runtime offers it only when the case's pack lists it (`packLocales` on the
 * exchange summary, be V223). An India case never offers it.
 *
 * PROVENANCE (binding, see AR_DECK_PROVENANCE): MACHINE-DRAFTED translation of the English deck, 2026-10-31. Not a
 * native-speaker review, not a tenant-ratified text. Every string here is a translation of the English source of
 * record — nothing is added. The legal notice bodies the customer consents to are the SERVER's (`notices[].body`,
 * tenant-ratified per VS-08 / D-10) and are not in this deck. Before a Gulf tenant goes live the deck rides the tenant's
 * linguistic ratification; until then the English text is the reference version (`sv.consent.noticeControlling`).
 */
export const AR_DECK_PROVENANCE = {
  locale: 'ar',
  status: 'machine-draft',
  ratification: 'awaiting tenant ratification',
  draftedOn: '2026-10-31',
  source: 'en',
} as const;

export const AR_DECK: Record<keyof typeof SV_COPY, string> = {
  'sv.subtitle': 'التحقق من الموقع',
  'sv.action.continue': 'متابعة',
  'sv.action.tryAgain': 'حاول مرة أخرى',
  'sv.action.retake': 'إعادة الالتقاط',
  'sv.action.back': 'رجوع',
  'sv.action.useThis': 'استخدم هذه',
  'sv.action.useAnyway': 'استخدمها على أي حال',
  'sv.action.allowed': 'لقد سمحت بالوصول',
  'sv.action.copyLink': 'نسخ رابط التحقق',
  'sv.action.linkCopied': 'تم نسخ الرابط ✓',
  'sv.action.startRecording': 'بدء التسجيل',
  'sv.action.stopRecording': 'إيقاف التسجيل',
  'sv.action.takePhoto': 'التقاط صورة',
  'sv.action.flipCamera': 'تبديل الكاميرا',
  'sv.action.turnOnCamera': 'تشغيل الكاميرا',
  'sv.action.submit': 'إرسال',
  'sv.action.reRecord': 'سجّل هذه مرة أخرى',
  'sv.action.skipShot': 'تخطَّ هذه اللقطة',
  'sv.pill.secure': 'آمن',
  'sv.pill.submitted': 'تم الإرسال',
  'sv.poweredBy': 'بدعم من KYC Hub',
  'sv.error.tryAgain': 'يُرجى المحاولة مرة أخرى.',

  // ── entry / shell ──────────────────────────────────────────────────────────
  'sv.entry.preparing': 'جارٍ فتح طلب التحقق الخاص بك…',
  'sv.entry.linkRequired.title': 'افتح رابط التحقق الخاص بك',
  'sv.entry.linkRequired.body': 'للبدء أو المتابعة، افتح الرابط الآمن الذي وصلك عبر رسالة نصية أو بريد إلكتروني.',
  'sv.entry.notFound.title': 'هذا الرابط غير صالح',
  'sv.entry.notFound.body': 'يُرجى استخدام أحدث رابط وصلك عبر الرسالة النصية أو البريد الإلكتروني.',
  'sv.entry.expired.title': 'انتهت صلاحية هذا الرابط',
  'sv.entry.expired.body': 'الروابط صالحة لمدة محدودة. اطلب من الجهة التي أرسلته أن ترسل إليك رابطًا جديدًا.',
  'sv.entry.closed.title': 'لم يعد هذا الطلب مفتوحًا',
  'sv.entry.closed.body':
    'ربما أُرسل بالفعل أو سُحب. إذا كنت تتوقع أن تسجّل شيئًا، فتواصل مع الجهة التي أرسلت الرابط.',
  'sv.entry.unreachable.title': 'تعذّر فتح طلبك',
  'sv.entry.unreachable.body': 'تحقق من اتصالك وحاول مرة أخرى. لم يُسجَّل أي شيء بعد.',

  // ── front door (confirm the request) ───────────────────────────────────────
  'sv.door.title': 'أكّد هذا الطلب',
  'sv.door.sub': 'قبل تسجيل أي شيء، تأكّد أن هذا الطلب هو ما كنت تتوقعه.',
  'sv.door.client': 'الجهة الطالبة',
  'sv.door.purpose': 'الغرض',
  'sv.door.purpose.CAPTURE': 'فحص مرئي لمقرك',
  'sv.door.purpose.RECAPTURE': 'تسجيل بعض اللقطات مرة أخرى',
  'sv.door.purpose.REVISIT': 'زيارة متابعة لمقرك',
  'sv.door.purpose.REVERIFY': 'فحص جديد لمقرك',
  'sv.door.purpose.FIELD_VISIT': 'زيارة من موظف ميداني',
  'sv.door.purpose.other': 'فحص لمقرك',
  'sv.door.reference': 'المرجع',
  'sv.door.premises': 'المقر',
  'sv.door.kind.OFFICE': 'مكتب',
  'sv.door.kind.SHOP': 'متجر',
  'sv.door.kind.FACTORY': 'مصنع',
  'sv.door.kind.WAREHOUSE': 'مستودع',
  'sv.door.kind.RESIDENCE': 'منزل',
  'sv.door.kind.OTHER': 'مقر',
  'sv.door.captureList': 'ما سيُطلب منك التقاطه',
  'sv.door.time': 'يستغرق ذلك نحو 12 إلى 20 دقيقة، على هذا الهاتف، في المقر.',
  'sv.door.never': 'لن تطلب منك {tenantName} أبدًا رموز تحقق أو كلمات مرور أو مستندات خارج هذه الصفحة.',
  'sv.door.neverGeneric': 'لن تطلب منك الجهة أبدًا رموز تحقق أو كلمات مرور أو مستندات خارج هذه الصفحة.',
  'sv.door.concern.trigger': 'هل يبدو شيء غير صحيح؟ أبلغ عن مخاوفك',
  'sv.door.concern.title': 'إذا بدا لك هذا الطلب غير صحيح',
  'sv.door.concern.body':
    'لا تتابع. تواصل مع {tenantName} عبر وسيلة تثق بها مسبقًا — رقم أو عنوان كان لديك قبل هذه الرسالة، لا رقمًا أو عنوانًا من هذه الصفحة.',
  'sv.door.concern.body2':
    'لا يُسجَّل أي شيء حتى تتابع، ولن يطلب منك أحد من الجهة رمزًا أو كلمة مرور أو صورة مستند خارج هذه الخطوات.',
  'sv.door.concern.stop': 'توقّف وأغلق هذه الصفحة',
  'sv.door.concern.stopped.title': 'لم يُسجَّل أي شيء',
  'sv.door.concern.stopped.body': 'يمكنك إغلاق هذه الصفحة. إذا قررت المتابعة لاحقًا، فافتح الرابط نفسه مرة أخرى.',
  'sv.door.cta': 'هذا ما كنت أتوقعه — متابعة',

  // ── OTP front door ─────────────────────────────────────────────────────────
  'sv.otp.title': 'أدخل الرمز الذي أرسلناه إليك',
  'sv.otp.sent.EMAIL': 'أرسلنا رمزًا من 6 أرقام إلى {target}. تحقق من بريدك الوارد، بما في ذلك مجلد البريد غير المرغوب فيه.',
  'sv.otp.sent.SMS': 'أرسلنا رمزًا من 6 أرقام برسالة نصية إلى {target}.',
  'sv.otp.validFor': 'يعمل الرمز لمدة 10 دقائق، وعلى هذه الصفحة فقط.',
  'sv.otp.label': 'رمز من 6 أرقام',
  'sv.otp.verify': 'تأكيد الرمز',
  'sv.otp.verifying': 'جارٍ التحقق من الرمز…',
  'sv.otp.never': 'لن نطلب منك هذا الرمز أبدًا عبر الهاتف أو الرسائل أو شخصيًا. أدخله هنا فقط.',
  'sv.otp.resend': 'لم يصلك رمز؟ اطلب من الجهة التي أرسلت إليك الرابط أن ترسله مرة أخرى.',
  'sv.otp.wrong': 'هذا الرمز غير صحيح. تحقق منه وحاول مرة أخرى.',
  'sv.otp.wrong.attempts': 'هذا الرمز غير صحيح. لديك {n} محاولات أخرى قبل أن يُقفَل هذا الرابط.',
  'sv.otp.wrong.last': 'هذا الرمز غير صحيح. لديك محاولة واحدة أخرى — بعدها سيُقفَل هذا الرابط.',
  'sv.otp.locked.title': 'هذا الرابط مقفل',
  'sv.otp.locked.body':
    'أُدخلت رموز غير صحيحة كثيرة، لذلك لم يعد هذا الرابط يعمل. يجب أن ترسل إليك الجهة رابطًا جديدًا. لم يُسجَّل أي شيء.',
  'sv.otp.expired.title': 'انتهت صلاحية هذا الرمز',
  'sv.otp.expired.body': 'تعمل الرموز لمدة 10 دقائق. اطلب من الجهة التي أرسلت إليك الرابط أن ترسله مرة أخرى.',
  'sv.otp.unavailable.title': 'تعذّر إرسال رمز إليك',
  'sv.otp.unavailable.body':
    'وسيلة إرسال الرموز لدى هذه الجهة غير متاحة الآن، لذلك لا يمكن فتح هذا الرابط بعد. لم يُسجَّل أي شيء. تواصل مع الجهة عبر وسيلة تثق بها مسبقًا.',
  'sv.otp.unreachable': 'تعذّر التحقق من الرمز. تحقق من اتصالك وحاول مرة أخرى.',
  'sv.otp.failed': 'تعذّر التحقق من الرمز الآن. يُرجى المحاولة مرة أخرى.',

  // ── consent ────────────────────────────────────────────────────────────────
  'sv.consent.title': 'موافقتك',
  'sv.consent.sub': 'اختر ما يجوز لـ {tenantName} تسجيله واستخدامه. البنود المعلّمة بأنها مطلوبة لازمة لهذا التحقق.',
  'sv.consent.notice': 'إصدار الإشعار {version}',
  'sv.consent.required': 'مطلوب',
  'sv.consent.optional': 'اختياري',
  'sv.consent.purpose.PREMISES_VIDEO.t': 'فيديو وصور للمقر',
  'sv.consent.purpose.PREMISES_VIDEO.d': 'مقاطع قصيرة وصور لخارج المقر وداخله، تُحفظ سجلًا لهذا التحقق.',
  'sv.consent.purpose.PERSONS_IN_FRAME.t': 'الأشخاص الذين يظهرون في التسجيل',
  'sv.consent.purpose.PERSONS_IN_FRAME.d':
    'وجهك في الصورة الذاتية، وأي شخص يظهر في الإطار مصادفةً. تُطمس الوجوه غير وجهك في النسخ المُشارَكة.',
  'sv.consent.purpose.GEOLOCATION.t': 'موقعك أثناء التسجيل',
  'sv.consent.purpose.GEOLOCATION.d': 'موقع الهاتف عند كل لقطة، مقارنةً بالعنوان المسجَّل.',
  'sv.consent.purpose.IDENTITY_DOCUMENT.t': 'وثيقة هويتك',
  'sv.consent.purpose.IDENTITY_DOCUMENT.d': 'صورة لهويتك، تُستخدم فقط للتأكد من أنك الشخص الذي يمثّل هذا المقر.',
  'sv.consent.purpose.LOCATION.t': 'موقعك أثناء التسجيل',
  'sv.consent.purpose.LOCATION.d': 'موقع الهاتف عند كل لقطة — وكيف حُصل عليه — مقارنةً بالعنوان المسجَّل.',
  'sv.consent.purpose.REGISTRY_CROSS_MATCH.t': 'مراجعة سجلات السجل العام',
  'sv.consent.purpose.REGISTRY_CROSS_MATCH.d':
    'تُستخدم معرّفات النشاط التجاري المقدَّمة مسبقًا إلى {tenantName} (مثل GSTIN أو Udyam) فقط للبحث عن السجل العام المطابق.',
  'sv.consent.purpose.DATA_RETENTION.t': 'الاحتفاظ بالسجل المختوم',
  'sv.consent.purpose.DATA_RETENTION.d':
    'يُحتفَظ بالتسجيل والبيانات المذكورة أعلاه، مختومة ودون تعديل، للمدة المذكورة في الإشعار، ثم تُحذف.',
  'sv.consent.noticeFull': 'الإشعار الكامل',
  'sv.consent.noticeControlling': '(النص الإنجليزي هو النسخة المرجعية)',
  'sv.consent.noticeControlling.ar': '(النص العربي هو النسخة المرجعية)',
  'sv.consent.purpose.ONWARD_SHARING.t': 'مشاركة النتيجة مع طرف آخر',
  'sv.consent.purpose.ONWARD_SHARING.d':
    'قد يُشارَك تقرير التحقق والتسجيلات مع المؤسسة الخاضعة للتنظيم التي تُجري {tenantName} التحقق نيابةً عنها.',
  'sv.consent.residence':
    'هذا منزل. سيُظهر التسجيل المكان الذي تسكن فيه. لا يظهر الموقع الدقيق على مستوى الشارع في النسخ المُشارَكة، ويُستخدم التسجيل فقط لتأكيد هذا العنوان.',
  'sv.consent.dwelling':
    'هذا سكن يعيش فيه أشخاص. صوِّر المدخل والمناطق المشتركة فقط — لا تصوِّر أبدًا غرف النوم أو أماكن النوم أو الأشخاص الذين يعيشون هنا. تُظهر النسخ المُشارَكة المنطقة فقط، لا الموقع الدقيق أبدًا.',
  'sv.consent.bystander.t': 'أبقِ الآخرين خارج الإطار',
  'sv.consent.bystander.d':
    'قبل أن تسجّل في الداخل، اطلب من كل من لا يلزم ظهوره في التسجيل أن يتنحّى جانبًا. لا تسجّل الأشخاص إلا حيث لا يمكنك تجنّب ذلك.',
  'sv.consent.retention':
    'يُحتفَظ بالتسجيلات للمدة التي حددتها الجهة. يمكنك أن تطلب منها التصحيح أو الحذف بعد انتهاء غرض التحقق.',
  'sv.consent.affirm': 'أنا **أوافق** على أن تسجّل {tenantName} البنود التي سمحت بها أعلاه وتستخدمها، لهذا التحقق.',
  'sv.consent.agree': 'أوافق وأتابع',
  'sv.consent.recording': 'جارٍ تسجيل موافقتك…',
  'sv.consent.recorded': 'سُجّلت الموافقة',
  'sv.consent.declined.title': 'لا يمكن متابعة هذا التحقق',
  'sv.consent.declined.body':
    'لم يُسمح ببند مطلوب: {purpose}. من دونه لا يمكن إكمال هذا التحقق بهذه الطريقة. يمكنك تغيير اختيارك، أو إغلاق هذه الصفحة والتواصل مع الجهة لخيار آخر.',
  'sv.consent.declined.review': 'مراجعة اختياراتي',
  'sv.consent.postfail': 'تعذّر تسجيل موافقتك. تحقق من اتصالك وحاول مرة أخرى.',
  'sv.consent.live.group': 'لمكالمة الفيديو المباشرة مع موظف',
  'sv.consent.purpose.LIVE_VIDEO.t': 'يُسجَّل فيديو مباشر لك',
  'sv.consent.purpose.LIVE_VIDEO.d':
    'يراك موظف من {tenantName} ويتحدث معك في مكالمة فيديو مسجَّلة؛ ووجهك وصوتك جزء من التسجيل.',
  'sv.consent.purpose.LIVE_IDENTITY_CHECK.t': 'فحص الحضور الحي ومقارنة الوجه',
  'sv.consent.purpose.LIVE_IDENTITY_CHECK.d':
    'يفحص برنامج أن الفيديو يُظهر شخصًا حاضرًا حيًّا، ويقارن وجهك بالصورة الذاتية التي التقطتها من قبل. شخصٌ هو من يقرر النتيجة.',
  'sv.consent.purpose.LIVE_CUSTODY_RETENTION.t': 'يُحتفَظ بتسجيل المكالمة وفق قواعد {tenantName} للاحتفاظ بمكالمات الفيديو',
  'sv.consent.purpose.LIVE_CUSTODY_RETENTION.d': 'لا وفق قواعد التحقق من الموقع المذكورة أعلاه. المدة مذكورة في الإشعار الكامل.',

  // ── capture flow ───────────────────────────────────────────────────────────
  'sv.capture.preparing': 'جارٍ تجهيز خطوات الالتقاط…',
  'sv.capture.startFail.title': 'تعذّر تحميل خطوات الالتقاط',
  'sv.capture.startFail.body': 'يُرجى التحقق من اتصالك والمحاولة مرة أخرى. لم يُفقد أي شيء.',
  'sv.capture.progress': 'الخطوة {n} من {total}',
  'sv.capture.mandatory': 'مطلوب',
  'sv.capture.optional': 'اختياري — يمكنك تخطيه',
  'sv.capture.clip': 'مقطع قصير',
  'sv.capture.still': 'صورة',
  'sv.capture.orientation.LANDSCAPE': 'أمسك هاتفك بالعرض (أفقيًا)',
  'sv.capture.orientation.PORTRAIT': 'أمسك هاتفك بالطول (عموديًا)',
  'sv.capture.orientation.ANY': 'أي اتجاه',
  'sv.capture.orientation.rotate': 'أدِر هاتفك ليطابق ذلك',
  'sv.capture.duration': 'سجّل ما بين {min} و{max} ثانية',
  'sv.capture.durationMin': '{min} ثانية على الأقل',
  'sv.capture.elapsed': '{s} ث',
  'sv.capture.recording': 'جارٍ التسجيل',
  'sv.capture.challenge.SHOW_NONCE': 'اعرض الرمز داخل الإطار',
  'sv.capture.challenge.SPEAK_NONCE': 'انطق الرمز بصوت مسموع',
  'sv.capture.challenge.ACTION': 'افعل هذا أثناء التسجيل',
  'sv.capture.challenge.why': 'يُثبت هذا أن المقطع سُجّل الآن، لهذا الطلب.',
  'sv.capture.visitCode': 'رمز الزيارة {code}',
  'sv.capture.micOff':
    'الميكروفون مغلق، لذلك لا يمكن سماع الرمز المنطوق. اسمح بالميكروفون، وإلا فقد يطلب منك المراجِع تسجيل هذا مرة أخرى.',
  'sv.capture.camera.badge.rear': 'الكاميرا الخلفية',
  'sv.capture.camera.badge.front': 'الكاميرا الأمامية',
  'sv.capture.camera.badge.camera': 'الكاميرا',
  'sv.capture.camera.badge.preview': 'معاينة',
  'sv.capture.camera.primer': 'تعمل الكاميرا عندما تضغط على الزر. لا يُسجَّل أي شيء حتى تبدأ.',
  'sv.capture.camera.starting': 'جارٍ تشغيل الكاميرا…',
  'sv.capture.camera.checking': 'جارٍ التحقق من إذن الكاميرا…',
  'sv.capture.camera.notready': 'ما زالت الكاميرا تبدأ — انتظر لحظة وحاول مرة أخرى.',
  'sv.capture.motion.primer': 'قد يطلب هاتفك مشاركة بيانات الحركة. يساعد ذلك على إثبات أن المقطع سُجّل باليد، هنا.',
  'sv.capture.hint.lineup': 'اضبط اللقطة وثبّت يدك.',
  'sv.action.flipShort': 'تبديل',
  'sv.action.skipShort': 'تخطٍّ',
  'sv.capture.grid.label': 'الشبكة',
  'sv.capture.grid.hide': 'إخفاء الشبكة',
  'sv.capture.grid.show': 'إظهار الشبكة',
  'sv.capture.level.level': 'مستوٍ',
  'sv.capture.level.left': '{deg}° إلى اليسار',
  'sv.capture.level.right': '{deg}° إلى اليمين',
  'sv.capture.location.aria': 'الموقع الذي ستسجّله هذه اللقطة',
  'sv.capture.location.waiting': 'في انتظار تحديد الموقع…',
  'sv.capture.location.denied': 'الوصول إلى الموقع مغلق — تُسجَّل اللقطة من دونه',
  'sv.capture.location.unsupported': 'الموقع غير متاح على هذا الجهاز — تُسجَّل اللقطة من دونه',
  'sv.capture.location.held': 'ثُبّت تحديد الموقع لهذه اللقطة',
  'sv.capture.location.bound': 'مرتبط بهذه اللقطة',
  'sv.capture.location.accuracy': '± {m} م حسب ما أفاد به الجهاز',
  'sv.capture.location.distance': 'المسافة إلى العنوان المسجَّل: يقيسها الخادم بعد الالتقاط',
  'sv.capture.location.justNow': 'الآن',
  'sv.capture.location.agoS': 'قبل {s} ث',
  'sv.capture.location.agoMin': 'قبل {m} د',
  'sv.capture.location.footer': 'ما ستسجّله هذه اللقطة',
  'sv.action.details': 'التفاصيل',
  'sv.action.detailsHide': 'إخفاء التفاصيل',
  'sv.capture.stamp.plusCode': 'رمز Plus {code}',
  'sv.capture.stamp.distance.about': 'على بُعد {d} تقريبًا من العنوان المسجَّل',
  'sv.capture.stamp.band.B50': 'ضمن 50 م',
  'sv.capture.stamp.band.B200': 'ضمن 200 م',
  'sv.capture.stamp.band.B1000': 'ضمن 1 كم',
  'sv.capture.stamp.band.OVER': 'أكثر من 1 كم',
  'sv.capture.stamp.distance.centroid': 'المسافة إلى العنوان المسجَّل: غير محددة (مركز الرمز البريدي فقط)',
  'sv.capture.stamp.copyNote': 'لا تُطبع الإحداثيات على النسخ المُشارَكة',
  'sv.capture.stamp.address': 'العنوان المسجَّل (كما لدى {tenantName})',
  'sv.capture.stamp.watch.denied': 'الوصول إلى الموقع مغلق — يُعرض آخر موقع أعطاه هذا الهاتف',
  'sv.capture.stamp.watch.unavailable': 'تحديثات الموقع غير متاحة — يُعرض آخر موقع',
  'sv.capture.stamp.watch.timeout': 'لا يوجد تحديد جديد بعد — يُعرض آخر موقع',
  'sv.capture.stamp.watch.unsupported': 'تحديثات الموقع غير متاحة على هذا الجهاز — يُعرض آخر موقع',
  'sv.capture.stamp.map.unavailable': 'الخريطة غير متاحة الآن',
  'sv.capture.stamp.map.title': 'الخريطة',
  'sv.capture.stamp.map.open': 'فتح الخريطة',
  'sv.capture.stamp.map.close': 'إغلاق الخريطة',
  'sv.capture.stamp.map.here': 'أنت هنا',
  'sv.capture.stamp.map.fixed': 'عرض ثابت حول موقعك — لا تحريك ولا تكبير في هذا الإصدار',
  'sv.capture.stamp.map.anchorOff': 'العنوان المسجَّل خارج هذه الخريطة، {side}',
  'sv.capture.stamp.map.side.N': 'باتجاه الشمال',
  'sv.capture.stamp.map.side.NE': 'باتجاه الشمال الشرقي',
  'sv.capture.stamp.map.side.E': 'باتجاه الشرق',
  'sv.capture.stamp.map.side.SE': 'باتجاه الجنوب الشرقي',
  'sv.capture.stamp.map.side.S': 'باتجاه الجنوب',
  'sv.capture.stamp.map.side.SW': 'باتجاه الجنوب الغربي',
  'sv.capture.stamp.map.side.W': 'باتجاه الغرب',
  'sv.capture.stamp.map.side.NW': 'باتجاه الشمال الغربي',
  'sv.capture.hint.selfie': 'أبقِ وجهك كله داخل الشكل البيضاوي، في إضاءة متساوية.',
  'sv.capture.hint.doc': 'أبقِ المستند كله داخل المربع، مسطّحًا، ومن دون انعكاس ضوء.',
  'sv.capture.doc.guide.title': 'قبل أن تصوّر المستند',
  'sv.capture.doc.guide.frame': 'ضع البطاقة مسطّحة وأبقِها كلها داخل المربع، مع ظهور الزوايا الأربع.',
  'sv.capture.doc.guide.glare': 'تجنّب الانعكاس والظلال: ابتعد عن النوافذ والمصابيح، وأبقِ الفلاش مغلقًا.',
  'sv.capture.doc.guide.lens': 'استخدم الكاميرا الخلفية، وثبّت الهاتف، ودعه يضبط التركيز قبل أن تلتقط الصورة.',
  'sv.capture.doc.stored': 'تُخزَّن صورة مستندك دليلًا على هذه الزيارة، بالحماية نفسها التي تشمل كل لقطة تلتقطها هنا، ولا يراها إلا من يراجعون حالتك. وحيثما كُتب الرقم كاملًا، لا تظهر إلا آخر 4 خانات منه.',
  'sv.capture.doc.storedMasked': 'قبل تخزين هذه الصورة، تُحجب أول 8 أرقام من رقم Aadhaar، وأي رقم VID، وأي رمز QR عليها. لا يراها إلا من يراجعون حالتك، ولا يُكتب الرقم أبدًا إلا بآخر 4 خانات منه.',
  'sv.capture.doc.storedPolicy': 'تُخزَّن صورة هويتك دليلًا على هذه الزيارة، بالحماية نفسها التي تشمل كل لقطة تلتقطها هنا، ولا يراها إلا من يراجعون حالتك.',
  'sv.capture.hint.dark': 'تبدو مظلمة جدًا — ابحث عن إضاءة أكثر.',
  'sv.capture.hint.bright': 'تبدو ساطعة جدًا — ابتعد عن الضوء المباشر.',
  'sv.capture.hint.blurry': 'تبدو غير واضحة — ثبّت يدك ودع الكاميرا تضبط التركيز.',
  'sv.capture.assessing': 'جارٍ فحص الجودة على جهازك…',
  'sv.capture.preview.ok': 'تبدو صالحة للاستخدام — استخدمها، أو التقطها مرة أخرى.',
  'sv.capture.preview.notEvaluated': 'تعذّر فحص الجودة على هذا الجهاز. سيفحصها المراجِع.',
  'sv.capture.face.none': 'لم نتمكن من رؤية وجه. التقط الصورة مرة أخرى ووجهك داخل الشكل البيضاوي.',
  'sv.capture.face.notEvaluated': 'سيُجرى فحص الوجه عند مراجعة صورتك.',
  'sv.capture.queued': 'حُفظت على هذا الهاتف — يجري الرفع في الخلفية.',
  'sv.capture.uploading': 'ما زالت {n} أجزاء قيد الرفع',
  'sv.capture.refused.title': 'تعذّر قبول هذه اللقطة كما أُرسلت',
  'sv.capture.refused.body':
    'لم يتمكن الخادم من التأكد من أن هذا التسجيل يطابق ما أرسله هاتفك، لذلك لم يُقبل. يُرجى تسجيله مرة أخرى.',
  'sv.capture.refused.code': 'رمز السبب {code}',
  'sv.capture.refused.doc.title': 'تعذّر علينا استخدام صورة مستندك هذه',
  'sv.capture.refused.doc.body': 'الصورة ليست واضحة بما يكفي، أو أن المستند ليس من المستندات التي نقبلها. التقطها مرة أخرى والمستند مسطّح وفي إضاءة جيدة. نقبل Aadhaar أو PAN أو جواز السفر أو رخصة القيادة أو بطاقة الناخب.',
  'sv.capture.refused.doc.bodyPolicy': 'الصورة ليست واضحة بما يكفي لاستخدامها. التقطها مرة أخرى والمستند كله مسطّح وفي إضاءة جيدة.',
  'sv.capture.refused.photo.title': 'تعذّر علينا استخدام هذه الصورة',
  'sv.capture.refused.photo.body': 'تعذّر فتح هذه الصورة. يُرجى التقاط صورة جديدة.',
  'sv.capture.sealed': 'مقبولة',
  'sv.capture.footer': 'سُجّلت على جهازك، وفُحصت جودتها هنا، وخُتمت بسلسلة تجزئة عند الرفع.',
  'sv.capture.bystander.title': 'قبل أن تسجّل في الداخل',
  'sv.capture.bystander.body':
    'اطلب من كل من لا يلزم ظهوره في التسجيل أن يتنحّى جانبًا. إذا كان لا بد من ظهور أشخاص، فلا بأس — تُطمس وجوههم في النسخ المُشارَكة.',
  'sv.capture.bystander.cta': 'فهمت — متابعة',
  'sv.capture.recaptureOnly': 'طُلبت مرة أخرى اللقطات أدناه فقط. يُحتفَظ بكل ما أرسلته غير ذلك.',
  'sv.capture.signature.primer':
    'قد يطلب هاتفك بصمة إصبعك أو وجهك لتوقيع التسجيل. يوقّعه ذلك بمفتاح على هذا الجهاز؛ ولا يشارك بصمتك.',
  'sv.capture.signature.unavailable':
    'لا يستطيع هذا الجهاز توقيع التسجيلات. ما زال التقاطك مقبولًا؛ ويرى المراجِع أنه لم يكن ممكنًا توقيعه من الجهاز.',
  'sv.gate.clip.short': 'هذا المقطع قصير جدًا. سجّل لمدة لا تقل عن الحد الأدنى المعروض.',
  'sv.gate.clip.empty': 'لم يُسجَّل أي شيء — يُرجى التسجيل مرة أخرى.',
  'sv.gate.clip.cameraStopped': 'توقفت الكاميرا — سجّل هذه الخطوة مرة أخرى.',
  'sv.gate.clip.dark': 'كان معظم هذا المقطع مظلمًا جدًا. ابحث عن إضاءة أكثر وسجّله مرة أخرى.',
  'sv.gate.clip.bright': 'كان معظم هذا المقطع ساطعًا جدًا. ابتعد عن الضوء المباشر وسجّله مرة أخرى.',
  'sv.gate.clip.blurry': 'كان هذا المقطع غير واضح. ثبّت يدك، ودع الكاميرا تضبط التركيز، وسجّله مرة أخرى.',
  'sv.gate.clip.notEvaluated': 'تعذّر فحص الجودة على هذا الجهاز.',
  'sv.gate.still.small': 'هذه الصورة صغيرة جدًا للحصول على نسخة واضحة. اقترب أو استخدم الكاميرا الخلفية.',
  'sv.gate.still.small.doc': 'هذه الصورة صغيرة جدًا ليكون المستند مقروءًا. اقترب أو استخدم الكاميرا الخلفية.',
  'sv.gate.still.dark': 'هذه الصورة مظلمة جدًا. ابحث عن إضاءة أكثر والتقطها مرة أخرى.',
  'sv.gate.still.dark.doc': 'المستند مظلم جدًا بحيث لا يمكن قراءته. ابحث عن إضاءة أكثر والتقطه مرة أخرى.',
  'sv.gate.still.bright': 'هذه الصورة ساطعة جدًا. ابتعد عن الضوء المباشر والتقطها مرة أخرى.',
  'sv.gate.still.glare.doc': 'يوجد انعكاس ضوء على المستند. ابتعد عن الضوء المباشر، وأبقِ الفلاش مغلقًا، والتقطه مرة أخرى.',
  'sv.gate.still.blurry': 'هذه الصورة غير واضحة. ثبّت يدك، ودع الكاميرا تضبط التركيز، والتقطها مرة أخرى.',
  'sv.gate.still.blurry.doc': 'المستند غير واضح. ثبّت الهاتف، ودع الكاميرا تضبط التركيز، والتقطه مرة أخرى.',
  'sv.gate.still.unreadable': 'تعذّرت قراءة تلك الصورة. يُرجى التقاطها مرة أخرى.',
  'sv.cam.denied.title': 'الوصول إلى الكاميرا محظور',
  'sv.cam.denied.body': 'أغلق متصفحك الوصول إلى الكاميرا لهذه الصفحة. اتبع الخطوات أدناه، ثم اضغط على الزر.',
  'sv.cam.osdenied.title': 'جهازك يحظر الكاميرا',
  'sv.cam.osdenied.body': 'لم تظهر نافذة طلب الإذن أبدًا، وهذا يعني عادةً أن أحد إعدادات النظام يحظر كاميرا المتصفح.',
  'sv.cam.nodevice.title': 'لم يُعثر على كاميرا',
  'sv.cam.nodevice.fallback': 'لم نتمكن من العثور على كاميرا صالحة للاستخدام على هذا الجهاز.',
  'sv.cam.nodevice.hookDetail': 'لم يُعثر على كاميرا على هذا الجهاز.',
  'sv.cam.stalled.title': 'الكاميرا لا ترسل صورة — اضغط لإعادة تشغيلها.',
  'sv.cam.stalled.restart': 'إعادة تشغيل الكاميرا',
  'sv.cam.webview.title': 'افتح هذا الرابط في متصفحك',
  'sv.cam.webview.body': 'لا يستطيع المتصفح المدمج في هذا التطبيق استخدام الكاميرا. انسخ الرابط وافتحه في Chrome أو Safari.',
  'sv.cam.unsupported.title': 'لا يستطيع هذا المتصفح تسجيل الفيديو',
  'sv.cam.unsupported.body': 'يُرجى فتح الرابط في إصدار حديث من Chrome أو Safari.',
  'sv.perm.steps.aria': 'خطوات السماح بالكاميرا',
  'sv.perm.webview.tip': 'نصيحة: اضغط على القائمة (⋮ أو أيقونة المشاركة) واختر «الفتح في المتصفح».',
  'sv.perm.rec.os.1': 'افتح الإعدادات في هاتفك.',
  'sv.perm.rec.os.2': 'ابحث عن متصفحك (Chrome أو Safari) وافتح أذوناته.',
  'sv.perm.rec.os.3': 'شغّل الكاميرا والميكروفون، ثم عُد إلى هنا.',
  'sv.perm.rec.chrome.1': 'اضغط على أيقونة القفل بجانب العنوان.',
  'sv.perm.rec.chrome.2': 'اضغط على الأذونات، ثم الكاميرا.',
  'sv.perm.rec.chrome.3': 'اختر السماح، ثم اضغط على الزر أدناه.',
  'sv.perm.rec.firefox.1': 'اضغط على أيقونة القفل بجانب العنوان.',
  'sv.perm.rec.firefox.2': 'اضغط على أذونات الموقع.',
  'sv.perm.rec.firefox.3': 'اضبط الكاميرا على السماح، ثم اضغط على الزر أدناه.',
  'sv.perm.rec.safari.1': 'اضغط على أيقونة «AA» في شريط العنوان.',
  'sv.perm.rec.safari.2': 'اضغط على إعدادات موقع الويب.',
  'sv.perm.rec.safari.3': 'اضبط الكاميرا على السماح، ثم اضغط على الزر أدناه.',
  'sv.perm.rec.generic.1': 'افتح إعدادات الموقع في متصفحك لهذه الصفحة.',
  'sv.perm.rec.generic.2': 'ابحث عن الكاميرا في قائمة الأذونات.',
  'sv.perm.rec.generic.3': 'اضبطها على السماح، ثم اضغط على الزر أدناه.',
  'sv.perm.osnotice.ios':
    'جهازك أقدم من الحد الأدنى الذي ندعمه ({min}). يمكنك المحاولة، لكن إذا تعطّلت الكاميرا فيُرجى التحديث أو استخدام هاتف آخر.',
  'sv.perm.osnotice.android':
    'متصفحك أقدم من الحد الأدنى الذي ندعمه ({min}). يمكنك المحاولة، لكن إذا تعطّلت الكاميرا فيُرجى التحديث أو استخدام هاتف آخر.',
  'sv.perm.osnotice.samsung':
    'متصفحك أقدم من الحد الأدنى الذي ندعمه ({min}). يمكنك المحاولة، لكن إذا تعطّلت الكاميرا فيُرجى التحديث أو استخدام هاتف آخر.',
  'sv.geo.gate.title': 'شغّل الموقع للمتابعة',
  'sv.geo.gate.why': 'تسجّل كل صورة وكل مقطع فيديو المكان الذي التُقط فيه، لكي تتمكّن {tenantName} من التأكد من المقر.',
  'sv.geo.gate.waiting': 'جارٍ تحديد موقعك…',
  'sv.geo.gate.off.lead': 'تعذّر علينا تحديد موقعك. قد تكون خدمة الموقع في هاتفك متوقفة.',
  'sv.geo.gate.denied.lead': 'الموقع محظور لهذه الصفحة. اسمح به للمتابعة.',
  'sv.geo.gate.off.android.1': 'اسحب من أعلى الشاشة إلى الأسفل لفتح الإعدادات السريعة.',
  'sv.geo.gate.off.android.2': 'اضغط على «الموقع» لتشغيله.',
  'sv.geo.gate.off.android.3': 'عُد إلى هنا. سنتحقق مرة أخرى تلقائيًا، أو اضغط «حاول مرة أخرى».',
  'sv.geo.gate.off.ios.1': 'افتح الإعدادات في هاتفك.',
  'sv.geo.gate.off.ios.2': 'انتقل إلى الخصوصية والأمن، ثم خدمات الموقع، وشغّلها.',
  'sv.geo.gate.off.ios.3': 'عُد إلى هنا. سنتحقق مرة أخرى تلقائيًا، أو اضغط «حاول مرة أخرى».',
  'sv.geo.gate.off.other.1': 'شغّل الموقع (GPS) من إعدادات جهازك.',
  'sv.geo.gate.off.other.2': 'تأكّد من أن هذا المتصفح مسموح له باستخدام موقعك.',
  'sv.geo.gate.off.other.3': 'عُد إلى هنا. سنتحقق مرة أخرى تلقائيًا، أو اضغط «حاول مرة أخرى».',
  'sv.geo.gate.off.android.app': 'الموقع مفعّل بالفعل؟ افتح الإعدادات، ثم التطبيقات، ثم متصفحك (مثل Chrome)، ثم الأذونات، ثم الموقع، واختر السماح.',
  'sv.geo.gate.off.ios.app': 'في القائمة نفسها، اضغط على {app} واختر «أثناء استخدام التطبيق».',
  'sv.geo.gate.retry': 'حاول مرة أخرى',
  'sv.geo.gate.ios.lead': 'لم يُسمح باستخدام الموقع لهذه الصفحة.',
  'sv.geo.gate.ios.1': 'اضغط «إعادة التحميل» أدناه لإعادة تحميل هذه الصفحة.',
  'sv.geo.gate.ios.2': 'عندما يطلب هاتفك استخدام موقعك، اضغط السماح.',
  'sv.geo.gate.ios.3': 'لم يُطلب منك ذلك؟ افتح الإعدادات، ثم الخصوصية والأمن، ثم خدمات الموقع، وتأكّد من تشغيلها — وكذلك لـ {app}.',
  'sv.geo.gate.reload': 'إعادة التحميل',
  'sv.geo.gate.refining': 'جارٍ تحديد موقع أدق — نحو ±{meters} م حتى الآن…',
  'sv.geo.gate.checking': 'جارٍ التحقق مرة أخرى…',
  'sv.geo.gate.timeout.lead': 'ما زلنا نبحث عن موقعك. اقترب من نافذة أو اخرج إلى مكان مكشوف — سنواصل المحاولة.',
  'sv.geo.gate.dismissed.lead': 'طلب متصفحك استخدام موقعك ولم يُسمح بذلك. اضغط «حاول مرة أخرى»، ثم اضغط السماح عندما يسألك المتصفح.',
  'sv.geo.gate.asking.lead': 'يطلب متصفحك استخدام موقعك. اضغط السماح عندما يسألك — أو اضغط «حاول مرة أخرى» ليُطلب منك ذلك من جديد.',
  'sv.geo.gate.webview.lead': 'لا يستطيع المتصفح المدمج في هذا التطبيق مشاركة موقعك أو استخدام الكاميرا. انسخ الرابط وافتحه في Chrome أو Safari.',
  'sv.review.title': 'جاهز للإرسال',
  'sv.review.sub': 'كل ما يلي محفوظ على هذا الهاتف. يكتمل الرفع في الخلفية قبل الإرسال.',
  'sv.review.waiting': 'في انتظار اكتمال الرفع…',
  'sv.review.missing.title': 'بعض اللقطات المطلوبة ناقصة',
  'sv.review.missing.body': 'يحتاج الخادم إلى هذه قبل أن يقبل إرسالك:',
  'sv.review.goTo': 'سجّلها الآن',
  'sv.review.submitFail': 'تعذّر الإرسال الآن. تسجيلاتك آمنة على هذا الهاتف — حاول مرة أخرى.',
  'sv.review.item.done': 'أُرسلت',
  'sv.review.item.pending': 'جارٍ الإرسال',
  'sv.review.item.refused': 'التقطها مرة أخرى',
  'sv.review.item.skipped': 'تم تخطيها',
  'sv.done.title': 'تم الإرسال',
  'sv.done.sub': 'شكرًا لك. استُلمت تسجيلاتك وسيراجعها شخص.',
  'sv.done.reference': 'المرجع',
  'sv.done.receipt': 'الإيصال',
  'sv.done.captured': 'الخطوات التي أكملتها',
  'sv.done.next': 'ماذا يحدث بعد ذلك',
  'sv.done.next.body':
    'يراجع موظف لدى {tenantName} التسجيلات. وهو من يقرر النتيجة — لا يقررها أي نظام آلي. إذا لزم شيء آخر، فسيصلك رابط جديد.',
  'sv.done.concern.title': 'الإبلاغ عن مخاوف',
  'sv.done.concern.body':
    'إذا أقلقك أي شيء في هذا الطلب، فتواصل مع {tenantName} عبر وسيلة تثق بها مسبقًا واذكر المرجع أعلاه.',
  'sv.done.limits.title': 'ما يُظهره هذا السجل',
  'sv.done.limits.chain':
    'خُتم كل تسجيل بسلسلة تجزئة على هاتفك أثناء إرساله، مرتبطًا بالوقت والموقع وهذا الطلب. يُظهر ذلك أن الخادم استلم بالضبط ما أرسله هذا الهاتف، وبالترتيب.',
  'sv.done.limits.signed': 'وُقّعت التسجيلات أيضًا بمفتاح على هذا الجهاز.',
  'sv.done.limits.unsigned': 'لم يتمكن هذا الجهاز من توقيع التسجيلات؛ ويرى المراجِع ذلك.',
  'sv.done.limits.geo.missing': 'لم يُسجَّل أي موقع على هذا الهاتف؛ ويرى المراجِع ذلك.',
  'sv.done.memory': 'لا يستمر الرفع على هذا الجهاز بعد إغلاق علامة التبويب. أبقِها مفتوحة حتى تظهر كل لقطة بحالة «أُرسلت».',
  'sv.done.close': 'يمكنك إغلاق هذه الصفحة.',
  'sv.live.card.title': 'انضم إلى التحقق المباشر',
  'sv.live.card.body': 'طلب موظف أن يرى مقرك في مكالمة مسجَّلة. انضم عندما تكون في المقر ومستعدًا.',
  'sv.live.card.action': 'انضم الآن',
  'sv.live.title': 'انضم إلى التحقق المباشر',
  'sv.live.sub': 'سيراك موظف من {tenantName} ويتحدث معك في مكالمة مسجَّلة.',
  'sv.live.pill.recording': 'جارٍ التسجيل',
  'sv.live.waiting.officer': 'في انتظار انضمام موظف…',
  'sv.live.connecting': 'جارٍ توصيل الكاميرا والميكروفون…',
  'sv.live.waiting.body': 'لم يتولَّ أي موظف مكالمتك بعد. تتحقق هذه الصفحة كل بضع ثوانٍ وتوصلك فور أن يتولاها أحدهم.',
  'sv.live.waiting.keepOpen': 'أبقِ هذه الصفحة مفتوحة. لا يُسجَّل أي شيء حتى ينضم الموظف.',
  'sv.live.expect.title': 'ما الذي تتوقعه',
  'sv.live.expect.officer': 'سيراك الموظف ويرى مقرك عبر الكاميرا.',
  'sv.live.expect.prompts': 'قد يطلب منك عرض أجزاء من المقر. تظهر طلباته على هذه الشاشة نصًّا.',
  'sv.live.expect.recorded': 'تُسجَّل المكالمة وتحتفظ بها {tenantName} مع تسجيلاتك السابقة.',
  'sv.live.action.leave': 'المغادرة الآن',
  'sv.live.action.hangUp': 'مغادرة المكالمة',
  'sv.live.action.rejoin': 'العودة إلى المكالمة',
  'sv.live.reconnecting': 'جارٍ إعادة الاتصال…',
  'sv.live.officerHere': 'الموظف في المكالمة.',
  'sv.live.officerJoining': 'تم الاتصال. الموظف ينضم الآن.',
  'sv.live.media.permission': 'لا نستطيع الوصول إلى الكاميرا أو الميكروفون. يُرجى السماح بالوصول في متصفحك، ثم العودة إلى المكالمة.',
  'sv.live.media.other': 'نواجه صعوبة في توصيل الكاميرا والميكروفون. يُرجى إعادة المحاولة بعد لحظة.',
  'sv.live.prompts.title': 'طلبات الموظف',
  'sv.live.prompts.none': 'لا توجد طلبات بعد. سيرشدك الموظف.',
  'sv.live.prompt.kind.PAN': 'حرّك الكاميرا ببطء',
  'sv.live.prompt.kind.HOLD': 'ثبّت الكاميرا',
  'sv.live.prompt.kind.WALK': 'امشِ والكاميرا تعمل',
  'sv.live.prompt.kind.READ': 'اقرأ بصوت مسموع',
  'sv.live.prompt.kind.OTHER': 'يطلب الموظف',
  'sv.live.prompt.done': 'تم — لقد عرضت هذا',
  'sv.live.prompt.history': 'الطلبات السابقة',
  'sv.live.prompt.state.COMPLETED': 'تم',
  'sv.live.prompt.state.EXPIRED': 'انتهى الوقت',
  'sv.live.prompt.state.CANCELLED': 'سحبه الموظف',
  'sv.live.consent.lead': 'قبل أن تنضم، تحتاج مكالمة الموظف المباشرة إلى موافقتك على البنود أدناه.',
  'sv.live.ended.title': 'أنهى الموظف الجلسة',
  'sv.live.ended.body': 'شكرًا لك. سُجّلت المكالمة وأُضيفت إلى طلبك لدى {tenantName}. سيراجعها شخص.',
  'sv.live.left.title': 'غادرت المكالمة',
  'sv.live.left.body': 'قد يكون الموظف ما زال ينتظر. يمكنك العودة ما دامت الجلسة مفتوحة.',
  'sv.live.unavailable.title': 'لا توجد مكالمة مباشرة للانضمام إليها',
  'sv.live.unavailable.body': 'هذا الطلب لا ينتظر مكالمة مباشرة. إذا طُلب منك الانضمام إلى مكالمة، فتواصل مع الجهة التي أرسلت الرابط.',
  'sv.live.superseded.title': 'فُتحت هذه المكالمة على جهاز آخر',
  'sv.live.superseded.body': 'تابع هناك. فُصلت هذه الصفحة.',
  'sv.live.unreachable.title': 'تعذّر الوصول إلى المكالمة',
  'sv.live.unreachable.body': 'تحقق من اتصالك وحاول مرة أخرى.',
  'sv.help.button': 'مساعدة',
  'sv.help.title': 'مساعدة',
  'sv.help.close': 'إغلاق',
  'sv.help.why.title': 'لماذا هذه اللقطة؟',
  'sv.help.why.generic': 'يساعد هذا {tenantName} على التأكد من أن المكان حقيقي ومستخدَم.',
  'sv.help.why.EXT': 'يساعد هذا {tenantName} على مطابقة اسم متجرك ولوحته مع سجلاتها.',
  'sv.help.why.ENTRANCE': 'يُري هذا {tenantName} طريق الدخول — وأن الباب حقيقي ويمكن الوصول إليه.',
  'sv.help.why.INT': 'يُري هذا {tenantName} منطقة العمل من الداخل، في دورة واحدة.',
  'sv.help.why.DOOR_PLATE': 'يتيح هذا لـ {tenantName} قراءة رقم الوحدة أو الباب المسجَّل لديها.',
  'sv.help.why.CERT_WALL': 'يتيح هذا لـ {tenantName} رؤية التسجيلات التي تعرضها.',
  'sv.help.why.SIGNBOARD': 'يتيح هذا لـ {tenantName} قراءة اسم النشاط التجاري ورقم GST بوضوح من صورة واضحة.',
  'sv.help.why.ACTIVITY': 'يُري هذا {tenantName} أن العمل يجري هنا يومًا بعد يوم.',
  'sv.help.why.INSTALL_POINT': 'يُري هذا {tenantName} المكان الذي سيُركَّب فيه الاتصال.',
  'sv.help.why.HOME': 'يُري هذا {tenantName} عنوان المنزل الذي قدّمته، من الخارج.',
  'sv.help.why.REP_SELFIE': 'يتيح هذا لـ {tenantName} رؤية من يُجري التحقق.',
  'sv.help.why.ID_DOCUMENT': 'يتيح هذا لـ {tenantName} مطابقة اسمك مع المستند الذي تحمله.',
  'sv.help.why.CHALLENGE': 'يُثبت هذا أن التسجيل أُجري الآن، لهذا الطلب.',
  'sv.help.why.screen': 'يتحقق هذا من أن المكان المسجَّل لدى {tenantName} حقيقي ومستخدَم.',
  'sv.help.good.title': 'كيف تبدو اللقطة الجيدة',
  'sv.help.showMe': 'أرِني',
  'sv.help.cap.generic': 'اضبط اللقطة',
  'sv.help.cant.title': 'ماذا تفعل إذا لم تستطع',
  'sv.help.skip.title': 'تخطَّ هذه اللقطة',
  'sv.help.skip.why': 'أخبرنا بالسبب (اختياري)',
  'sv.help.skip.reason.reach': 'لا أستطيع الوصول إلى هذا المكان',
  'sv.help.skip.reason.none': 'هذا غير موجود هنا',
  'sv.help.skip.reason.unsafe': 'ليس آمنًا الآن',
  'sv.help.skip.reason.other': 'سبب آخر',
  'sv.help.skip.confirm': 'تخطَّ هذه',
  'sv.help.skip.note': 'تخطّي لقطة اختيارية لا يعطّل طلبك أبدًا.',
  'sv.help.call.line': 'تحتاج إلى مساعدة؟ اطلب منا أن نتصل بك.',
  'sv.help.call.button': 'اطلب مكالمة',
  'sv.help.call.reason': 'بماذا تحتاج إلى مساعدة؟ (اختياري)',
  'sv.help.call.sending': 'جارٍ الإرسال…',
  'sv.help.call.failed': 'تعذّر إرسال ذلك. حاول مرة أخرى.',
  'sv.help.recovery.title': 'هل الكاميرا أو الموقع محظوران؟',
  'sv.perm.platform.android': 'على Android (Chrome)',
  'sv.perm.platform.ios': 'على iPhone',
  'sv.perm.platform.other': 'في متصفحك',
  'sv.perm.ios.app.other': 'تطبيق متصفحك',
  'sv.perm.cam.title': 'شغّل الكاميرا',
  'sv.perm.cam.ios.1': 'افتح الإعدادات في هاتفك.',
  'sv.perm.cam.ios.2': 'انتقل إلى {app}، ثم الكاميرا.',
  'sv.perm.cam.ios.3': 'اختر «السماح»، ثم عُد إلى هنا واضغط على الزر أدناه.',
  'sv.perm.loc.title': 'شغّل الموقع',
  'sv.perm.loc.android.1': 'اضغط على أيقونة القفل بجانب العنوان.',
  'sv.perm.loc.android.2': 'اضغط على الأذونات، ثم الموقع.',
  'sv.perm.loc.android.3': 'اختر السماح، ثم عُد إلى هنا.',
  'sv.perm.loc.ios.1': 'افتح الإعدادات في هاتفك.',
  'sv.perm.loc.ios.2': 'انتقل إلى الخصوصية والأمن، ثم خدمات الموقع، ثم {app}.',
  'sv.perm.loc.ios.3': 'اختر «أثناء استخدام التطبيق»، ثم عُد إلى هنا.',
  'sv.perm.loc.other.1': 'افتح إعدادات الموقع في متصفحك لهذه الصفحة.',
  'sv.perm.loc.other.2': 'ابحث عن الموقع في قائمة الأذونات.',
  'sv.perm.loc.other.3': 'اضبطه على السماح، ثم عُد إلى هنا.',
  'sv.perm.rec.then': 'لن يضيع أي شيء أثناء قيامك بذلك.',
  'sv.capture.location.howTo': 'كيفية السماح',
  'sv.queue.saved': 'محفوظة على هذا الهاتف · ستُرسل',
  'sv.queue.savedOffline': 'محفوظة على هذا الهاتف · ستُرسل عند عودة الاتصال',
  'sv.queue.keepOpen': 'أبقِ هذه الصفحة مفتوحة حتى يتم الإرسال',
  'sv.queue.onePiece': 'ما زال جزء واحد قيد الرفع',
  'sv.consent.companion': 'يمكنك القيام بذلك بمساعدة أحد أفراد أسرتك — فقط لا تُظهره في اللقطات.',
  'sv.lang.title': 'اختر لغتك',
  'sv.lang.sub': 'يمكنك تغييرها في أي وقت.',
  'sv.lang.suggested': 'مقترحة',
  'sv.lang.lowData.title': 'استخدام بيانات أقل',
  'sv.lang.lowData.sub': 'لا تُحمَّل الخرائط والشروحات المرئية إلا عندما تطلبها.',
  'sv.lang.lowData.auto': 'مفعّل لهذا الاتصال',
  'sv.hint.dark': 'الإضاءة خافتة قليلًا',
  'sv.hint.moving': 'اثبت مكانك',
  'sv.hint.closer': 'اقترب أكثر',
  'sv.hint.back': 'ابتعد قليلًا',
  'sv.affirm.good': 'لقطة جيدة',
  'sv.capture.stamp.map.show': 'إظهار الخريطة',
  'sv.review.skipReason': 'تم التخطي — {reason}',
  'sv.showme.STREET.cap': 'قف في الجهة المقابلة من الشارع',
  'sv.showme.SIDEWAYS.cap': 'أدِر هاتفك بالعرض',
  'sv.showme.DOOR.cap': 'امشِ إلى الباب',
  'sv.showme.TURN.cap': 'استدر ببطء، مرة واحدة',
  'sv.showme.PLATE.cap': 'اقترب من لوحة الاسم',
  'sv.showme.CERTIFICATES.cap': 'اعرض الشهادات المعلّقة على الجدار',
  'sv.showme.FACE.cap': 'انظر مباشرة إلى الكاميرا',
  'sv.showme.ID_FLAT.cap': 'أمسك هويتك مسطّحة وفي إضاءة جيدة',
  'sv.showme.title': 'كيف تلتقط هذه',
  'sv.showme.gotIt': 'فهمت',
  'sv.showme.close': 'إغلاق',
  'sv.showme.next': 'التالي',
  'sv.showme.again': 'اعرض مرة أخرى',
  'sv.audio.replay': 'تشغيل التعليمات مرة أخرى.',
  'sv.audio.mute': 'إيقاف صوت الإرشاد',
  'sv.audio.unmute': 'تشغيل صوت الإرشاد',
  'sv.audio.unavailable': 'الإرشاد الصوتي غير متاح بلغة {language} بعد — اقرأ الخطوات أدناه.',
  'sv.audio.listenNotice': 'استمع إلى هذا الإشعار',
  'sv.audio.listenNotice.aid':
    'يمكنك الاستماع إلى هذا الإشعار أو قراءته. وفي الحالتين، ستحتاج إلى الضغط على «{action}» عندما تكون مستعدًا.',
  'sv.audio.stop': 'إيقاف',
};
